import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { couponApi } from '../../services/api';

const C = {
  card:'#ffffff', border:'#f0e4d8', border2:'#f5ede4',
  text1:'#1a0f00', text3:'#6b4c35', text4:'#a07858',
  accent:'#c96428', accentL:'#fff4ee', accentB:'#f0d0b0',
  surface:'#fffaf7', green:'#16a34a', greenL:'#dcfce7',
  red:'#ef4444', redL:'#fee2e2',
};

const inp = {
  width:'100%', padding:'10px 14px', borderRadius:10, fontSize:14,
  border:`1.5px solid ${C.border}`, background:C.surface,
  color:C.text1, fontFamily:'inherit', outline:'none', boxSizing:'border-box',
};
const focusS = e => { e.target.style.borderColor=C.accent; e.target.style.boxShadow='0 0 0 3px rgba(201,100,40,0.1)'; };
const blurS  = e => { e.target.style.borderColor=C.border; e.target.style.boxShadow='none'; };
const Label  = ({children}) => <label style={{display:'block',color:C.text3,fontSize:11,fontWeight:700,marginBottom:6,textTransform:'uppercase',letterSpacing:0.5}}>{children}</label>;

const EMPTY = { code:'', description:'', type:'percentage', value:'', min_order_amount:'', max_discount:'', usage_limit:'', is_active:true, expires_at:'' };

const CouponModal = ({ coupon, onClose, onSaved }) => {
  const [form, setForm] = useState(coupon ? {
    ...coupon,
    expires_at: coupon.expires_at ? coupon.expires_at.slice(0,10) : '',
    min_order_amount: coupon.min_order_amount || '',
    max_discount: coupon.max_discount || '',
    usage_limit: coupon.usage_limit || '',
  } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.value) return toast.error('Code and value required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : 0,
        max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
        expires_at: form.expires_at || null,
      };
      if (coupon?.id) { await couponApi.update(coupon.id, payload); toast.success('Coupon updated!'); }
      else            { await couponApi.create(payload);            toast.success('Coupon created!'); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        style={{position:'absolute',inset:0,background:'rgba(26,15,0,0.55)',backdropFilter:'blur(4px)'}} onClick={onClose}/>
      <motion.div initial={{opacity:0,scale:0.95,y:20}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}}
        style={{position:'relative',background:C.card,borderRadius:22,width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 32px 80px rgba(0,0,0,0.2)',border:`1px solid ${C.border}`}}>

        {/* Header */}
        <div style={{padding:'22px 24px 18px',borderBottom:`1px solid ${C.border2}`,background:`linear-gradient(135deg,${C.accentL},#fff)`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:44,height:44,borderRadius:13,background:C.accentL,border:`1.5px solid ${C.accentB}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>🎟️</div>
            <div>
              <p style={{fontFamily:'"Playfair Display",serif',fontSize:17,fontWeight:700,color:C.text1,margin:0}}>{coupon?'Edit Coupon':'Create Coupon'}</p>
              <p style={{color:C.text4,fontSize:12,margin:'2px 0 0'}}>{coupon?'Update coupon details':'Add a new discount coupon'}</p>
            </div>
          </div>
          <button onClick={onClose} style={{width:34,height:34,borderRadius:10,border:`1px solid ${C.border}`,background:C.card,cursor:'pointer',color:C.text3,fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>

          {/* Code */}
          <div>
            <Label>Coupon Code *</Label>
            <input placeholder="e.g. WELCOME10, DIWALI20" value={form.code}
              onChange={e=>set('code',e.target.value.toUpperCase())}
              style={{...inp,fontFamily:'monospace',fontWeight:700,letterSpacing:1,fontSize:15}}
              onFocus={focusS} onBlur={blurS}/>
            <p style={{color:C.text4,fontSize:11,marginTop:4}}>Auto-converted to uppercase</p>
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <input placeholder="e.g. Welcome discount for new customers" value={form.description}
              onChange={e=>set('description',e.target.value)} style={inp} onFocus={focusS} onBlur={blurS}/>
          </div>

          {/* Type + Value */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <Label>Discount Type *</Label>
              <select value={form.type} onChange={e=>set('type',e.target.value)} style={inp} onFocus={focusS} onBlur={blurS}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>
            <div>
              <Label>Value *</Label>
              <div style={{position:'relative'}}>
                <input type="number" min="0" step="0.01" placeholder={form.type==='percentage'?'e.g. 10':'e.g. 50'}
                  value={form.value} onChange={e=>set('value',e.target.value)}
                  style={{...inp,paddingRight:36}} onFocus={focusS} onBlur={blurS}/>
                <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',color:C.accent,fontWeight:800,fontSize:14}}>
                  {form.type==='percentage'?'%':'₹'}
                </span>
              </div>
            </div>
          </div>

          {/* Preview */}
          {form.value && (
            <div style={{padding:'12px 16px',borderRadius:12,background:C.accentL,border:`1px solid ${C.accentB}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{color:C.text3,fontSize:13}}>Discount on ₹500 order</span>
              <span style={{color:C.accent,fontWeight:800,fontSize:15}}>
                -₹{form.type==='percentage'
                  ? Math.min(500*parseFloat(form.value||0)/100, form.max_discount?parseFloat(form.max_discount):9999).toFixed(0)
                  : Math.min(parseFloat(form.value||0),500).toFixed(0)}
              </span>
            </div>
          )}

          {/* Min order + Max discount */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <Label>Min Order Amount (₹)</Label>
              <input type="number" min="0" placeholder="0 = no minimum"
                value={form.min_order_amount} onChange={e=>set('min_order_amount',e.target.value)}
                style={inp} onFocus={focusS} onBlur={blurS}/>
            </div>
            {form.type==='percentage' && (
              <div>
                <Label>Max Discount (₹)</Label>
                <input type="number" min="0" placeholder="Leave blank = no limit"
                  value={form.max_discount} onChange={e=>set('max_discount',e.target.value)}
                  style={inp} onFocus={focusS} onBlur={blurS}/>
              </div>
            )}
          </div>

          {/* Usage limit + Expiry */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <Label>Usage Limit</Label>
              <input type="number" min="1" placeholder="Leave blank = unlimited"
                value={form.usage_limit} onChange={e=>set('usage_limit',e.target.value)}
                style={inp} onFocus={focusS} onBlur={blurS}/>
            </div>
            <div>
              <Label>Expiry Date</Label>
              <input type="date" value={form.expires_at} onChange={e=>set('expires_at',e.target.value)}
                style={inp} onFocus={focusS} onBlur={blurS}/>
            </div>
          </div>

          {/* Active toggle */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,background:form.is_active?C.greenL:'#f5f5f5',border:`1px solid ${form.is_active?'#a5d6a7':C.border}`}}>
            <div>
              <p style={{fontWeight:700,color:form.is_active?C.green:C.text3,margin:0,fontSize:14}}>{form.is_active?'✅ Active':'⭕ Inactive'}</p>
              <p style={{color:C.text4,fontSize:12,margin:'2px 0 0'}}>Customers can use this coupon</p>
            </div>
            <div onClick={()=>set('is_active',!form.is_active)}
              style={{width:52,height:28,borderRadius:99,cursor:'pointer',position:'relative',background:form.is_active?C.green:C.border,transition:'all 0.25s',boxShadow:form.is_active?'0 2px 8px rgba(22,163,74,0.4)':'none'}}>
              <div style={{position:'absolute',top:4,width:20,height:20,background:'white',borderRadius:'50%',transition:'transform 0.25s',boxShadow:'0 1px 4px rgba(0,0,0,0.2)',transform:form.is_active?'translateX(26px)':'translateX(4px)'}}/>
            </div>
          </div>

          {/* Buttons */}
          <div style={{display:'flex',gap:12,paddingTop:4}}>
            <button type="button" onClick={onClose}
              style={{flex:1,padding:'12px 0',borderRadius:12,border:`1.5px solid ${C.border}`,background:'#fff',color:C.text3,fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{flex:2,padding:'12px 0',borderRadius:12,border:'none',background:saving?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)',color:'#fff',fontSize:14,fontWeight:700,cursor:saving?'not-allowed':'pointer',fontFamily:'inherit',boxShadow:saving?'none':'0 4px 16px rgba(201,100,40,0.35)'}}>
              {saving?'⏳ Saving...':`${coupon?'✅ Update':'➕ Create'} Coupon`}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [deleteId,setDeleteId]= useState(null);
  const [search,  setSearch]  = useState('');

  const fetchCoupons = async () => {
    setLoading(true);
    try { const r = await couponApi.getAll(); setCoupons(r.data.data||[]); }
    catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ fetchCoupons(); },[]);

  const handleDelete = async (id) => {
    try { await couponApi.delete(id); toast.success('Coupon deleted!'); setDeleteId(null); fetchCoupons(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (c) => {
    try { await couponApi.update(c.id,{...c,is_active:!c.is_active}); fetchCoupons(); toast.success(c.is_active?'Coupon deactivated':'Coupon activated'); }
    catch { toast.error('Failed to update'); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Code "${code}" copied!`); };

  const filtered = coupons.filter(c =>
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    (c.description||'').toLowerCase().includes(search.toLowerCase())
  );

  const active   = coupons.filter(c=>c.is_active).length;
  const expired  = coupons.filter(c=>c.expires_at&&new Date(c.expires_at)<new Date()).length;
  const totalUsed= coupons.reduce((s,c)=>s+(c.used_count||0),0);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:24}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontFamily:'"Playfair Display",serif',fontSize:26,fontWeight:800,color:C.text1,margin:0}}>Coupons</h1>
          <p style={{color:C.text4,fontSize:13,marginTop:4}}>Manage discount codes for customers</p>
        </div>
        <button onClick={()=>setModal('new')}
          style={{padding:'11px 24px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#c96428,#9a4a1e)',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:8,boxShadow:'0 4px 14px rgba(201,100,40,0.35)'}}>
          🎟️ Create Coupon
        </button>
      </div>

      {/* Summary cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14}}>
        {[
          {label:'Total Coupons',value:coupons.length,icon:'🎟️',color:'#2563eb',colorL:'#dbeafe'},
          {label:'Active',value:active,icon:'✅',color:C.green,colorL:C.greenL},
          {label:'Expired',value:expired,icon:'⏰',color:'#d97706',colorL:'#fef3c7'},
          {label:'Total Used',value:totalUsed,icon:'📊',color:C.accent,colorL:C.accentL},
        ].map(s=>(
          <div key={s.label} style={{background:C.card,borderRadius:14,padding:'16px 18px',border:`1px solid ${C.border}`,boxShadow:'0 2px 8px rgba(0,0,0,0.04)',display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:40,height:40,borderRadius:11,background:s.colorL,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{s.icon}</div>
            <div>
              <p style={{color:C.text4,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:0.4,margin:0}}>{s.label}</p>
              <p style={{color:s.color,fontSize:20,fontWeight:800,margin:'2px 0 0'}}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{position:'relative',maxWidth:360}}>
        <span style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',fontSize:16,color:C.text4}}>🔍</span>
        <input placeholder="Search coupons..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{...inp,paddingLeft:42,borderRadius:12}} onFocus={focusS} onBlur={blurS}/>
      </div>

      {/* Coupons grid */}
      {loading ? (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:16}}>
          {Array(4).fill(0).map((_,i)=><div key={i} style={{height:180,borderRadius:18,background:'#f5ede4',animation:'pulse 1.5s infinite'}}/>)}
        </div>
      ) : filtered.length===0 ? (
        <div style={{textAlign:'center',padding:'64px 24px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
          <div style={{fontSize:48,marginBottom:12}}>🎟️</div>
          <p style={{color:C.text3,fontSize:16,fontWeight:600,margin:0}}>{search?'No coupons found':'No coupons yet'}</p>
          <p style={{color:C.text4,fontSize:13,marginTop:6}}>Create your first coupon to start offering discounts</p>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
          {filtered.map(c=>{
            const isExpired = c.expires_at && new Date(c.expires_at) < new Date();
            const usagePct  = c.usage_limit ? Math.min((c.used_count/c.usage_limit)*100,100) : 0;
            return (
              <motion.div key={c.id} layout whileHover={{y:-3,boxShadow:'0 8px 32px rgba(0,0,0,0.10)'}}
                style={{background:C.card,borderRadius:18,border:`1px solid ${isExpired?'#fecaca':c.is_active?C.accentB:C.border}`,overflow:'hidden',boxShadow:'0 2px 12px rgba(0,0,0,0.05)',opacity:isExpired||!c.is_active?0.8:1}}>

                {/* Card header */}
                <div style={{padding:'16px 18px',background:isExpired?C.redL:c.is_active?C.accentL:'#f5f5f5',borderBottom:`1px solid ${isExpired?'#fecaca':c.is_active?C.accentB:C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontFamily:'monospace',fontSize:18,fontWeight:800,color:isExpired?C.red:c.is_active?C.accent:C.text3,letterSpacing:1}}>{c.code}</span>
                    <button onClick={()=>copyCode(c.code)} title="Copy code"
                      style={{padding:'3px 8px',borderRadius:6,background:'transparent',border:`1px solid ${isExpired?'#fecaca':C.accentB}`,cursor:'pointer',fontSize:11,color:isExpired?C.red:C.accent,fontWeight:600}}>
                      Copy
                    </button>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:99,
                    background:isExpired?C.redL:c.is_active?C.greenL:'#f5f5f5',
                    color:isExpired?C.red:c.is_active?C.green:C.text4,
                    border:`1px solid ${isExpired?'#fecaca':c.is_active?'#a5d6a7':C.border}`}}>
                    {isExpired?'Expired':c.is_active?'Active':'Inactive'}
                  </span>
                </div>

                <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:12}}>
                  {/* Discount value */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div>
                      <p style={{color:C.text4,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:0.4,margin:0}}>Discount</p>
                      <p style={{color:C.accent,fontSize:22,fontWeight:800,margin:'2px 0 0'}}>
                        {c.type==='percentage'?`${c.value}%`:`₹${c.value}`}
                        {c.type==='percentage'&&c.max_discount&&<span style={{fontSize:12,color:C.text4,fontWeight:500}}> (max ₹{c.max_discount})</span>}
                      </p>
                    </div>
                    {c.min_order_amount>0&&(
                      <div style={{textAlign:'right'}}>
                        <p style={{color:C.text4,fontSize:11,margin:0}}>Min order</p>
                        <p style={{color:C.text3,fontSize:14,fontWeight:700,margin:'2px 0 0'}}>₹{c.min_order_amount}</p>
                      </div>
                    )}
                  </div>

                  {c.description&&<p style={{color:C.text4,fontSize:12,margin:0,lineHeight:1.5}}>{c.description}</p>}

                  {/* Usage bar */}
                  {c.usage_limit && (
                    <div>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                        <span style={{color:C.text4,fontSize:11}}>Usage</span>
                        <span style={{color:C.text3,fontSize:11,fontWeight:600}}>{c.used_count}/{c.usage_limit}</span>
                      </div>
                      <div style={{height:6,borderRadius:99,background:C.border2,overflow:'hidden'}}>
                        <div style={{height:'100%',borderRadius:99,background:usagePct>=100?C.red:C.accent,width:`${usagePct}%`,transition:'width 0.3s'}}/>
                      </div>
                    </div>
                  )}
                  {!c.usage_limit&&<p style={{color:C.text4,fontSize:11,margin:0}}>Used {c.used_count} times · No limit</p>}

                  {c.expires_at&&(
                    <p style={{color:isExpired?C.red:C.text4,fontSize:11,margin:0,fontWeight:isExpired?700:400}}>
                      {isExpired?'⛔ Expired':'⏰ Expires'}: {new Date(c.expires_at).toLocaleDateString('en-IN')}
                    </p>
                  )}

                  {/* Actions */}
                  <div style={{display:'flex',gap:8,paddingTop:4,borderTop:`1px solid ${C.border2}`}}>
                    <button onClick={()=>toggleActive(c)}
                      style={{flex:1,padding:'8px 0',borderRadius:10,border:`1px solid ${c.is_active?'#fecaca':C.accentB}`,background:c.is_active?C.redL:C.accentL,color:c.is_active?C.red:C.accent,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                      {c.is_active?'Deactivate':'Activate'}
                    </button>
                    <button onClick={()=>setModal(c)}
                      style={{flex:1,padding:'8px 0',borderRadius:10,border:`1px solid ${C.border}`,background:'#fff',color:C.text3,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                      ✏️ Edit
                    </button>
                    <button onClick={()=>setDeleteId(c.id)}
                      style={{padding:'8px 12px',borderRadius:10,border:'1px solid #fecaca',background:C.redL,color:C.red,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                      🗑️
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {modal && <CouponModal coupon={modal==='new'?null:modal} onClose={()=>setModal(null)} onSaved={fetchCoupons}/>}
      </AnimatePresence>

      <AnimatePresence>
        {deleteId && (
          <div style={{position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              style={{position:'absolute',inset:0,background:'rgba(26,15,0,0.55)',backdropFilter:'blur(4px)'}} onClick={()=>setDeleteId(null)}/>
            <motion.div initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}}
              style={{position:'relative',background:C.card,borderRadius:20,padding:32,width:'100%',maxWidth:340,textAlign:'center',boxShadow:'0 24px 64px rgba(0,0,0,0.2)',border:`1px solid ${C.border}`}}>
              <div style={{width:64,height:64,borderRadius:18,background:C.redL,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,margin:'0 auto 16px'}}>🗑️</div>
              <h3 style={{fontFamily:'"Playfair Display",serif',fontSize:18,fontWeight:700,color:C.text1,margin:'0 0 8px'}}>Delete Coupon?</h3>
              <p style={{color:C.text4,fontSize:13,margin:'0 0 24px'}}>This action cannot be undone.</p>
              <div style={{display:'flex',gap:12}}>
                <button onClick={()=>setDeleteId(null)}
                  style={{flex:1,padding:'11px 0',borderRadius:11,border:`1.5px solid ${C.border}`,background:'#fff',color:C.text3,fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                  Cancel
                </button>
                <button onClick={()=>handleDelete(deleteId)}
                  style={{flex:1,padding:'11px 0',borderRadius:11,border:'none',background:'linear-gradient(135deg,#ef4444,#dc2626)',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 14px rgba(239,68,68,0.35)'}}>
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.55}}`}</style>
    </div>
  );
};

export default AdminCoupons;
